import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2020 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, ButtonBase, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DelayedLoadIndicator } from '../../../components/delayed-load-indicator';
import { MainContext } from '../../../context';
import { MONO_FONT } from '../../../default/theme';
import { cardSurface, focusOutline } from '../../../components/page-primitives';
import { createRoute } from '../../../utils';
import { NamespaceDetailView } from '../../../components/namespace/namespace-detail-view';
import { NamespaceDetailConfigContext } from '../../../components/namespace/namespace-detail-config';
import { CreateNamespaceDialog } from './create-namespace-dialog';
import { UserSettingsRoutes } from '../user-settings-routes';
import { BackButton } from '../settings/settings-primitives';
import { useHandleNamespaceCreated, useUserNamespaces } from './use-user-namespaces';
// Mobile-only list entry; on desktop namespaces are picked from the sidebar.
const NamespaceListItem = styled(ButtonBase)(({ theme }) => ({
    ...cardSurface(theme),
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '0.8125rem',
    textAlign: 'left',
    padding: '0.875rem 1rem',
    marginBottom: '0.625rem',
    fontFamily: 'inherit',
    ...focusOutline(theme),
    '@media (hover: hover)': {
        '&:hover': { borderColor: theme.palette.secondary.main }
    }
}));
export const UserSettingsNamespaces = ({ selectedName }) => {
    const { pageSettings, service, user } = useContext(MainContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const namespacesQuery = useUserNamespaces();
    const handleNamespaceCreated = useHandleNamespaceCreated();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const namespaces = namespacesQuery.data ?? [];
    const loading = namespacesQuery.isLoading || detailLoading;
    const namespaceAccessUrl = pageSettings.urls.namespaceAccessInfo;
    // Pin the viewer's own membership and hide them from the add-member search.
    const detailConfig = useMemo(() => ({
        fixSelf: true,
        filterUsers: (foundUser) => foundUser.provider !== user?.provider || foundUser.loginName !== user?.loginName
    }), [user]);
    const openNamespace = (namespace) => {
        navigate(createRoute([UserSettingsRoutes.NAMESPACES, namespace.name]));
    };
    const renderDetail = (namespace) => (_jsx(NamespaceDetailConfigContext.Provider, { value: detailConfig, children: _jsx(NamespaceDetailView, { namespace: namespace, setLoadingState: setDetailLoading, extensionRoutePrefix: UserSettingsRoutes.EXTENSIONS, showClaimAction: true, 
            // The public endpoint hides what a namespace member still needs to manage: an
            // inactive extension, or one whose only versions are soft-deleted.
            extensionQuery: extension => ({
                queryKey: ['user', 'extension', namespace.name, extension.name],
                queryFn: ({ signal }) => service.getExtension(signal, namespace.name, extension.name)
            }) }) }));
    const renderList = () => (_jsxs(Box, { children: [namespaces.map(namespace => {
                const extensionCount = Object.keys(namespace.extensions).length;
                return (_jsxs(NamespaceListItem, { onClick: () => openNamespace(namespace), children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '0.4375rem' }, children: [_jsx(Typography, { component: 'span', noWrap: true, sx: { fontFamily: MONO_FONT, fontSize: '0.9375rem', fontWeight: 700, minWidth: 0 }, children: namespace.name }), !namespace.verified ? (_jsx(WarningAmberIcon, { titleAccess: 'Not verified', sx: { fontSize: '0.875rem', color: 'warningAccent', flexShrink: 0 } })) : null] }), _jsx(Typography, { sx: { fontSize: '0.78125rem', color: 'text.disabled', mt: '0.1875rem' }, children: extensionCount === 1 ? '1 extension' : `${extensionCount} extensions` })] }), _jsx(ChevronRightIcon, { sx: { fontSize: '1.125rem', color: 'text.disabled', flexShrink: 0 } })] }, namespace.name));
            }), _jsx(Button, { variant: 'outlined', startIcon: _jsx(AddIcon, {}), onClick: () => setCreateDialogOpen(true), children: "Create namespace" })] }));
    const renderContent = () => {
        if (namespaces.length === 0) {
            if (loading) {
                return null;
            }
            return (_jsxs(Box, { children: [_jsxs(Typography, { variant: 'body1', sx: { mb: '1rem' }, children: ["No namespaces available. Read", ' ', _jsx(Link, { color: 'secondary', href: namespaceAccessUrl, target: '_blank', children: "here" }), ' ', "about claiming namespaces."] }), _jsx(Button, { variant: 'outlined', startIcon: _jsx(AddIcon, {}), onClick: () => setCreateDialogOpen(true), children: "Create namespace" })] }));
        }
        if (isMobile && selectedName == null) {
            return renderList();
        }
        const chosenNamespace = namespaces.find(namespace => namespace.name === selectedName) ?? namespaces[0];
        return (_jsxs(Box, { children: [isMobile ? (_jsx(BackButton, { disableRipple: true, onClick: () => navigate(UserSettingsRoutes.NAMESPACES), startIcon: _jsx(ArrowBackIcon, { sx: { fontSize: '0.9375rem' } }), sx: { mb: '1.125rem' }, children: "All namespaces" })) : null, renderDetail(chosenNamespace)] }));
    };
    return (_jsxs(Box, { children: [_jsx(DelayedLoadIndicator, { loading: loading }), renderContent(), _jsx(CreateNamespaceDialog, { open: createDialogOpen, onClose: () => setCreateDialogOpen(false), namespaceCreated: handleNamespaceCreated })] }));
};
//# sourceMappingURL=user-settings-namespaces.js.map